import { useLocation, Link, Navigate } from "@/lib/router-compat";
import { ArrowRight, MapPin, CheckCircle, Star, Clock, Users, Home, Wallet } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import MagneticButton from "@/components/MagneticButton";
import Testimonials from "@/components/Testimonials";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { findAreaBySlug } from "@/data/local-areas";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

interface FeaturedProject {
  id: string;
  title: string;
  slug: string;
  cover_image: string | null;
  location: string | null;
}

const LocalAreaPage = () => {
  const location = useLocation(); const slug = location.pathname.replace(/^\//, "");
  const area = slug ? findAreaBySlug(slug) : undefined;
  const [projects, setProjects] = useState<FeaturedProject[]>([]);

  useEffect(() => {
    supabase
      .from("projects")
      .select("id, title, slug, cover_image, location")
      .eq("published", true)
      .order("display_order")
      .limit(6)
      .then(({ data }) => setProjects(data ?? []));
  }, []);

  if (!area) return <Navigate to="/architecte-interieur-lyon" replace />;

  const canonical = `/${area.slug}`;
  const url = `https://jiia-studio.com${canonical}`;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": url,
      name: `JIIA Studio – Architecte d'intérieur à ${area.city}`,
      description: area.intro,
      url,
      address: {
        "@type": "PostalAddress",
        addressLocality: area.cityShort,
        addressRegion: "Auvergne-Rhône-Alpes",
        postalCode: area.postalCode,
        addressCountry: "FR",
      },
      geo: { "@type": "GeoCoordinates", latitude: area.geo.lat, longitude: area.geo.lng },
      areaServed: { "@type": "City", name: area.city },
      priceRange: "€€",
      founder: { "@type": "Person", name: "Charline Jacob" },
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `Architecte d'intérieur ${area.city}`,
      provider: { "@type": "LocalBusiness", name: "JIIA Studio" },
      areaServed: { "@type": "City", name: area.city },
      serviceType: "Architecture d'intérieur",
      description: area.intro,
      offers: {
        "@type": "Offer",
        priceSpecification: { "@type": "PriceSpecification", price: "1500", priceCurrency: "EUR", description: "À partir de" },
      },
    },
  ];

  return (
    <Layout>
      <SEOHead
        title={`Architecte d'intérieur ${area.city} | JIIA Studio`}
        description={`${area.intro.slice(0, 145)}${area.intro.length > 145 ? "…" : ""}`}
        canonical={canonical}
        jsonLd={jsonLd}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Architecte d'intérieur Lyon", path: "/architecte-interieur-lyon" },
          { name: area.city, path: canonical },
        ]}
      />

      {/* Hero */}
      <section className="py-24 md:py-32 bg-secondary/30">
        <div className="container grid gap-12 lg:grid-cols-2 lg:items-center">
          <AnimatedSection>
            <div className="flex items-center gap-2 text-sm text-muted-foreground font-body mb-4">
              <MapPin className="h-4 w-4" />
              <span>{area.city} · {area.distance}</span>
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-light leading-tight mb-6">
              Architecte d'intérieur<br />
              <span className="italic">à {area.city}</span>
            </h1>
            <div className="bg-card border-l-4 border-accent p-5 mb-8">
              <p className="text-xs font-body uppercase tracking-widest text-accent mb-2">En bref</p>
              <p className="text-base text-foreground font-body leading-relaxed">
                {area.intro} Premier rendez-vous gratuit, devis sous 48h, accompagnement de A à Z.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-sm font-body text-sm uppercase tracking-widest hover:bg-primary/90 transition-colors">
                    Devis gratuit <ArrowRight className="h-4 w-4" />
                  </span>
                </MagneticButton>
              </Link>
              <Link to="/projets">
                <MagneticButton>
                  <span className="flex items-center gap-2 px-8 py-4 border border-border rounded-sm font-body text-sm uppercase tracking-widest hover:bg-secondary transition-colors">
                    Voir mes réalisations
                  </span>
                </MagneticButton>
              </Link>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <figure className="m-0">
              <img
                src={area.heroImage}
                alt={area.heroAlt}
                width={1600}
                height={1000}
                fetchPriority="high"
                decoding="sync"
                className="w-full aspect-[8/5] object-cover rounded-sm"
              />
              <figcaption className="mt-3 text-xs font-body text-muted-foreground">{area.heroCaption}</figcaption>
            </figure>
          </AnimatedSection>
        </div>
      </section>

      {/* Contexte local */}
      <section className="py-20 md:py-28">
        <div className="container max-w-4xl">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Contexte local</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-8">
              Pourquoi un architecte d'intérieur à {area.cityShort} ?
            </h2>
            <p className="text-lg text-foreground font-body leading-relaxed mb-6 whitespace-pre-line">
              {area.context}
            </p>
            <figure className="my-10">
              <img
                src={area.interiorImage}
                alt={area.interiorAlt}
                width={1400}
                height={1000}
                loading="lazy"
                className="w-full aspect-[7/5] object-cover rounded-sm"
              />
              <figcaption className="mt-3 text-xs font-body text-muted-foreground">{area.interiorCaption}</figcaption>
            </figure>
            <div className="grid gap-6 sm:grid-cols-2 mt-10">
              <div className="border border-border p-6 rounded-sm">
                <Home className="h-5 w-5 text-accent mb-3" />
                <h3 className="font-heading text-lg mb-2">Le bâti local</h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{area.housing}</p>
              </div>
              <div className="border border-border p-6 rounded-sm">
                <Wallet className="h-5 w-5 text-accent mb-3" />
                <h3 className="font-heading text-lg mb-2">Budgets observés</h3>
                <p className="font-body text-sm text-muted-foreground leading-relaxed">{area.budget}</p>
              </div>
            </div>
            <div className="mt-8">
              <h3 className="font-heading text-lg mb-3">Quartiers où j'interviens à {area.cityShort}</h3>
              <ul className="flex flex-wrap gap-2 list-none p-0">
                {area.neighborhoods.map((n) => (
                  <li key={n} className="text-sm font-body px-3 py-1.5 border border-border rounded-sm text-muted-foreground">
                    {n}
                  </li>
                ))}
              </ul>
            </div>
            {area.population && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-10">
                <div className="border border-border p-5 rounded-sm">
                  <Users className="h-5 w-5 text-accent mb-2" />
                  <p className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-1">Population</p>
                  <p className="font-heading text-xl">{area.population}</p>
                </div>
                <div className="border border-border p-5 rounded-sm">
                  <Clock className="h-5 w-5 text-accent mb-2" />
                  <p className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-1">Trajet</p>
                  <p className="font-heading text-xl">{area.distance}</p>
                </div>
                <div className="border border-border p-5 rounded-sm">
                  <MapPin className="h-5 w-5 text-accent mb-2" />
                  <p className="text-xs font-body uppercase tracking-widest text-muted-foreground mb-1">Code postal</p>
                  <p className="font-heading text-xl">{area.postalCode}</p>
                </div>
              </div>
            )}
          </AnimatedSection>
        </div>
      </section>

      {/* Spécificités */}
      <section className="py-20 md:py-28 bg-secondary/30">
        <div className="container">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Mes interventions</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
              Projets type à {area.cityShort}
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {area.specificities.map((spec, i) => (
              <AnimatedSection key={spec} delay={i * 0.1}>
                <div className="flex gap-4 p-6 bg-card border border-border rounded-sm hover:border-accent/50 transition-colors h-full">
                  <CheckCircle className="h-5 w-5 text-accent shrink-0 mt-1" />
                  <p className="font-body text-foreground leading-relaxed">{spec}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <AnimatedSection>
            <p className="text-base text-foreground font-body leading-relaxed mt-12 max-w-3xl">
              {area.examples}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Projets récents */}
      {projects.length > 0 && (
        <section className="py-20 md:py-28">
          <div className="container">
            <AnimatedSection>
              <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Réalisations</p>
              <h2 className="font-heading text-3xl md:text-4xl font-light mb-12">
                Réalisations récentes en Rhône-Alpes
              </h2>
            </AnimatedSection>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, i) => (
                <AnimatedSection key={project.id} delay={i * 0.1}>
                  <Link to={`/projets/${project.slug}`} className="group block">
                    <div className="aspect-[4/3] overflow-hidden rounded-sm mb-3">
                      <img
                        src={project.cover_image || ""}
                        alt={`Projet ${project.title} par JIIA Studio`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="font-heading text-lg group-hover:text-accent transition-colors">{project.title}</h3>
                    {project.location && (
                      <p className="text-xs text-muted-foreground font-body flex items-center gap-1 mt-1">
                        <MapPin className="h-3 w-3" /> {project.location}
                      </p>
                    )}
                  </Link>
                </AnimatedSection>
              ))}
            </div>
            <div className="mt-10 text-center">
              <Link to="/projets" className="inline-flex items-center gap-2 text-sm font-body text-accent hover:text-accent/80 transition-colors uppercase tracking-widest">
                Voir tous les projets <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      <Testimonials />

      {/* Questions fréquentes */}
      <section className="py-20 md:py-28 bg-secondary/30">
        <div className="container max-w-3xl">
          <AnimatedSection>
            <p className="text-xs font-body uppercase tracking-[0.3em] text-muted-foreground mb-3">Questions fréquentes</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-10">
              Ce qu'on me demande à {area.cityShort}
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {area.faq.map((item, i) => (
                <AccordionItem key={item.question} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-body">{item.question}</AccordionTrigger>
                  <AccordionContent className="font-body text-muted-foreground leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="container text-center">
          <AnimatedSection>
            <Star className="h-8 w-8 mx-auto mb-4 text-rose" />
            <h2 className="font-heading text-3xl md:text-4xl font-light mb-4">
              Votre projet à {area.cityShort} ?
            </h2>
            <p className="text-primary-foreground/70 font-body max-w-lg mx-auto mb-8 leading-relaxed">
              Premier rendez-vous gratuit et sans engagement. Discutons ensemble de votre projet d'aménagement,
              de rénovation ou de décoration à {area.city}.
            </p>
            <Link to="/contact">
              <MagneticButton>
                <span className="inline-flex items-center gap-2 px-10 py-4 bg-rose text-white rounded-sm font-body text-sm uppercase tracking-widest hover:bg-rose/90 transition-colors">
                  Demander un devis gratuit <ArrowRight className="h-4 w-4" />
                </span>
              </MagneticButton>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
};

export default LocalAreaPage;
